import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Trash2, Users, Building2 } from 'lucide-react';

const API = '';

const emptyBuyer = {
  buyer_name: '', company_name: '', billing_address: '', shipping_address: '',
  gst_number: '', contact_person: '', phone: '', email: ''
};

export function BuyerMaster() {
  const [buyers, setBuyers] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyBuyer });
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchBuyers = useCallback(async () => {
    try {
      const params = search ? { search } : {};
      const { data } = await axios.get(`${API}/api/buyers-master`, { params, withCredentials: true });
      setBuyers(data);
    } catch {
      toast.error('Failed to load buyers');
    }
  }, [search]);

  useEffect(() => { fetchBuyers(); }, [fetchBuyers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.buyer_name.trim()) { toast.error('Buyer Name is required'); return; }
    setLoading(true);
    try {
      if (editId) {
        await axios.put(`${API}/api/buyers-master/${editId}`, form, { withCredentials: true });
        toast.success('Buyer updated');
      } else {
        await axios.post(`${API}/api/buyers-master`, form, { withCredentials: true });
        toast.success('Buyer created');
      }
      setDialogOpen(false);
      setForm({ ...emptyBuyer });
      setEditId(null);
      fetchBuyers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving buyer');
    } finally { setLoading(false); }
  };

  const openEdit = (buyer) => {
    setEditId(buyer.id);
    setForm({
      buyer_name: buyer.buyer_name || '', company_name: buyer.company_name || '',
      billing_address: buyer.billing_address || '', shipping_address: buyer.shipping_address || '',
      gst_number: buyer.gst_number || '', contact_person: buyer.contact_person || '',
      phone: buyer.phone || '', email: buyer.email || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/api/buyers-master/${id}`, { withCredentials: true });
      toast.success('Buyer deleted');
      setDeleteConfirm(null);
      fetchBuyers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error deleting buyer');
    }
  };

  const openNew = () => { setEditId(null); setForm({ ...emptyBuyer }); setDialogOpen(true); };

  return (
    <div className="p-6 max-w-6xl" data-testid="buyer-master-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users size={24} /> Buyer Master
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Manage buyers for PO creation and challans</p>
        </div>
        <Button onClick={openNew} className="gap-2" data-testid="add-buyer-btn">
          <Plus size={16} /> Add Buyer
        </Button>
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search buyers..."
          className="pl-9 h-9"
          data-testid="buyer-search"
        />
      </div>

      {/* Buyers Table */}
      <div className="border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr>
              <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Buyer Name</th>
              <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Company</th>
              <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">GST Number</th>
              <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Contact</th>
              <th className="text-left p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Phone</th>
              <th className="text-right p-3 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {buyers.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-zinc-400">
                  <Building2 size={32} className="mx-auto mb-2 opacity-40" />
                  No buyers found. Add your first buyer above.
                </td>
              </tr>
            ) : (
              buyers.map((b) => (
                <tr key={b.id} className="border-t border-zinc-200 hover:bg-zinc-50/50" data-testid={`buyer-row-${b.id}`}>
                  <td className="p-3 font-medium">{b.buyer_name}</td>
                  <td className="p-3 text-zinc-600">{b.company_name || '-'}</td>
                  <td className="p-3 font-mono text-xs">{b.gst_number || '-'}</td>
                  <td className="p-3 text-zinc-600">{b.contact_person || '-'}</td>
                  <td className="p-3 text-zinc-600">{b.phone || '-'}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(b)} data-testid={`edit-buyer-${b.id}`}>
                        <Pencil size={14} />
                      </Button>
                      {deleteConfirm === b.id ? (
                        <div className="flex items-center gap-1">
                          <Button variant="destructive" size="sm" onClick={() => handleDelete(b.id)} data-testid={`confirm-delete-${b.id}`}>
                            Confirm
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(b.id)} data-testid={`delete-buyer-${b.id}`}>
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="buyer-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? 'Edit Buyer' : 'Add New Buyer'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs">Buyer Name *</Label>
                <Input value={form.buyer_name} onChange={(e) => setForm(p => ({ ...p, buyer_name: e.target.value }))}
                  placeholder="e.g. Myntra Designs Pvt Ltd" className="mt-1" data-testid="buyer-name-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Company Name</Label>
                <Input value={form.company_name} onChange={(e) => setForm(p => ({ ...p, company_name: e.target.value }))}
                  placeholder="Legal entity name" className="mt-1" data-testid="company-name-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Billing Address</Label>
                <Input value={form.billing_address} onChange={(e) => setForm(p => ({ ...p, billing_address: e.target.value }))}
                  placeholder="Full billing address" className="mt-1" data-testid="billing-address-input" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Shipping Address</Label>
                <Input value={form.shipping_address} onChange={(e) => setForm(p => ({ ...p, shipping_address: e.target.value }))}
                  placeholder="Full shipping address" className="mt-1" data-testid="shipping-address-input" />
              </div>
              <div>
                <Label className="text-xs">GST Number</Label>
                <Input value={form.gst_number} onChange={(e) => setForm(p => ({ ...p, gst_number: e.target.value }))}
                  placeholder="22AAAAA0000A1Z5" className="mt-1" data-testid="gst-number-input" />
              </div>
              <div>
                <Label className="text-xs">Contact Person</Label>
                <Input value={form.contact_person} onChange={(e) => setForm(p => ({ ...p, contact_person: e.target.value }))}
                  placeholder="Name" className="mt-1" data-testid="contact-person-input" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+91 XXXXX XXXXX" className="mt-1" data-testid="phone-input" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="buyer@company.com" className="mt-1" data-testid="email-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="save-buyer-btn">
                {loading ? 'Saving...' : editId ? 'Update Buyer' : 'Add Buyer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
